import { useState } from "react";
import { Pointer } from "rfc6902";
import { CollapseRow } from "../controls";
import { AddNewButton } from "./AddNewButton";
import { useBeFigure } from "./BeFigureContextProvider";
import { FigureDataRow } from "./FigureDataRow";
import { BeFigureAddOperations } from "./functions/interface";
import {
  FigureAxisData,
  FigureGroupData,
  FigureLegendData,
  FigureReferenceLinesData,
  FigureRegressionLineData,
} from "./types";
import { FigureVarSettings } from "./types/FigureVarSettings";

export interface FigureDataRowsProps {
  addType?: BeFigureAddOperations;
  dataPath: "/dataGroups" | "/legend" | "/referenceLines" | "/regressionLines" | "/axes";
  itemLabel: string;
  label: string;
  noAddRemove?: boolean;
  noNumber?: boolean;
  propertiesKey: string;
}

/**
 * Component to show the options for the data groups
 *
 * @param props - Destructured parameter object
 * @param props.addType - The operation type for adding a new item
 * @param props.dataPath - The path to the array in figureInput (e.g., "/dataGroups", "/legend", "/referenceLines", "/regressionLines", "/axes" )
 * @param props.itemLabel - The prefix for the item label (e.g., "Data series", "Legend item", "Reference line", "Regression line")
 * @param props.label - The label for the CollapseRow
 * @param props.noAddRemove - Whether to show the add/remove buttons
 * @param props.noNumber - Whether to show the number in the item label (e.g., "Data series 1", "Data series 2", etc.)
 * @param props.propertiesKey - The key for the properties array
 *        (e.g., "dataRows", "legendProperties", "lineProperties")
 */
export const FigureDataRows = ({
  addType,
  dataPath,
  itemLabel,
  label,
  noAddRemove = false,
  noNumber = false,
  propertiesKey,
}: FigureDataRowsProps): React.ReactElement | null => {
  const { state } = useBeFigure();
  const figureInput = state?.figureInput;
  const figureSettings = state?.figureSettings;
  const figureType = figureInput?.figureType;
  const figureTypeSettings = figureSettings?.figureTypeSettings.find(
    (fts) => fts.value === figureType,
  );

  const pointer = Pointer.fromJSON(dataPath);
  const item = pointer.get(figureInput);
  const itemArray = Array.isArray(item)
    ? (item as
        | FigureAxisData[]
        | FigureGroupData[]
        | FigureLegendData[]
        | FigureReferenceLinesData[]
        | FigureRegressionLineData[])
    : undefined;
  const [show, setShow] = useState<boolean>(false);

  return !itemArray ||
    typeof itemArray === "string" ||
    !figureType ||
    !figureSettings ||
    !figureTypeSettings ? null : (
    <CollapseRow
      label={label}
      isOpen={show}
      setIsOpen={setShow}
      className="border rounded border-gray-500 border-2 px-1"
    >
      <div>
        {itemArray.map((item, ix) => {
          let varSettings: FigureVarSettings[] = [];
          switch (dataPath) {
            case "/axes":
              varSettings =
                figureSettings.figureAxisSettings.find(
                  (ftas) => figureTypeSettings.requiredAxis[ix] === ftas.axisType,
                )?.parameters ??
                // istanbul ignore next
                [];
              break;
            case "/legend":
              varSettings =
                figureSettings.figureLegendSettings.find(
                  (ls) => ls.legendType === figureInput.legendType,
                )?.parameters ??
                // istanbul ignore next
                [];
              break;
            case "/referenceLines":
              varSettings = figureSettings.figureReferenceLinesSettings;
              break;
            case "/regressionLines":
              varSettings =
                figureSettings.figureRegressionLineSettings.find(
                  (fs) => fs.lineType === (item as FigureRegressionLineData).lineType,
                )?.parameters ??
                // istanbul ignore next
                [];
              break;
            case "/dataGroups":
            default:
              varSettings = figureTypeSettings.dataRowVars;
              break;
          }
          return (
            <FigureDataRow
              key={ix}
              id={item.id}
              dataPath={`${dataPath}/${ix}`}
              itemLabel={itemLabel}
              noNumber={noNumber}
              noRemove={noAddRemove}
              propertiesKey={propertiesKey}
              sibLingCount={itemArray.length}
              varSettings={varSettings}
            />
          );
        })}
        {!noAddRemove && addType && (
          <AddNewButton
            id={"add-data-row-btn"}
            operation={addType}
            newLength={itemArray.length + 1}
          />
        )}
      </div>
    </CollapseRow>
  );
};

FigureDataRows.displayName = "FigureDataRows";
